(() => {
  const OFFER_TAG = 'AI7_ADD_MORE_V1';
  const OFFER_CONTEXT = 'AI7_THEME_154071990446';
  const OFFER_PERCENT = 15;
  const OFFER_TITLE_MATCH = 'add more & save';
  const MINIMUM_QUALIFYING_PACK = 12;

  if (customElements.get('cca-add-more-save')) return;

  class CcaAddMoreSave extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.shortNames = new Map();
      const shortNamesNode = this.querySelector('[data-add-more-short-names]');
      if (shortNamesNode) {
        try {
          this.shortNames = new Map(Object.entries(JSON.parse(shortNamesNode.textContent)));
        } catch (error) {
          console.error('Add more product names could not be read.', error);
        }
      }
      this.products = new Map();
      this.querySelectorAll('[data-add-more-product]').forEach((node) => {
        try {
          const product = this.normalizeProduct(JSON.parse(node.textContent));
          this.products.set(product.handle, product);
        } catch (error) {
          console.error('Add more product data could not be read.', error);
        }
      });

      this.form = this.querySelector('[data-add-more-form]');
      this.formReveal = this.querySelector('[data-form-reveal]');
      this.choiceSystem = this.querySelector('[data-choice-system]');
      this.choiceReveal = this.querySelector('[data-choice-reveal]');
      this.heading = this.querySelector('.cca-add-more__heading');
      this.choiceList = this.querySelector('[data-product-choices]');
      this.optionFields = this.querySelector('[data-option-fields]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.button = this.querySelector('[data-add-more-button]');
      this.buttonAction = this.querySelector('[data-button-action]');
      this.rewardMessage = this.querySelector('[data-reward-message]');
      this.initialCartNode = this.querySelector('[data-initial-cart]');
      this.selectedSummary = this.querySelector('[data-selected-summary]');
      this.selectedImage = this.querySelector('[data-selected-image]');
      this.selectedTitle = this.querySelector('[data-selected-title]');
      this.basePrice = this.querySelector('[data-base-price]');
      this.offerPrice = this.querySelector('[data-offer-price]');
      this.priceAnnouncement = this.querySelector('[data-price-announcement]');
      this.quantityWrap = this.querySelector('[data-quantity-wrap]');
      this.purchaseActions = this.querySelector('[data-purchase-actions]');
      this.error = this.querySelector('[data-error]');
      this.browserButton = this.querySelector('[data-product-browser-open]');
      this.browserLabel = this.querySelector('[data-browser-label]');
      this.suggestionsToggle = this.querySelector('[data-suggestions-toggle]');
      this.dialog = this.querySelector('[data-product-browser]');
      this.priceWrap = this.querySelector('.cca-add-more__price');
      this.searchInput = this.querySelector('[data-product-search]');
      this.searchResults = this.querySelector('[data-search-results]');
      this.searchStatus = this.querySelector('[data-search-status]');
      const recommendationChoices = [...this.querySelectorAll('[data-product-choice]')];
      this.recommendationHandles = new Set(
        recommendationChoices.map((choice) => choice.dataset.productChoice),
      );
      this.choiceList.dataset.choiceCount = String(recommendationChoices.length);
      this.currentProductHandle = this.dataset.currentProductHandle;
      this.defaultProductHandle = recommendationChoices[0]?.dataset.productChoice
        || this.dataset.defaultProductHandle
        || this.currentProductHandle;
      this.mainVariantId = this.readMainVariantId() || Number(this.dataset.currentVariantId);
      this.offerActive = this.dataset.offerActive === 'true';
      this.cartReady = false;
      try {
        const initialCart = JSON.parse(this.initialCartNode?.textContent || 'null');
        if (initialCart && Array.isArray(initialCart.items)) {
          this.cart = initialCart;
          this.cartReady = true;
        }
      } catch (error) {
        console.error('The initial Add more cart state could not be read.', error);
      }
      // Preserve an in-flight cart transaction when Shopify reconnects the
      // same custom element after replacing its inner product markup.
      this.isAdding = this.isAdding === true;
      this.cartRequestId = Number(this.cartRequestId || 0);
      this.addRequestId = Number(this.addRequestId || 0);
      this.cartController = null;
      this.reconcilingCart = false;
      this.productRequestId = 0;
      this.searchTimer = null;
      this.searchRequestId = 0;
      this.searchController = null;
      this.productController = null;
      this.cartUpdateTimer = null;
      this.moneyFormatter = new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: this.dataset.currency || 'AUD',
      });

      this.onChoiceClick = (event) => {
        if (this.isAdding) return;
        const choice = event.target.closest('[data-product-choice]');
        if (choice) this.selectProduct(choice.dataset.productChoice, undefined, {source: 'recommendation', reveal: true});
      };
      this.onOptionChange = (event) => {
        if (this.isAdding) return;
        const option = event.target.closest('button[data-option-index][data-option-value]');
        if (option) this.resolveVariant(Number(option.dataset.optionIndex), option.dataset.optionValue);
      };
      this.onQuantityChange = () => {
        if (this.isAdding) return;
        this.quantityInput.value = String(Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1));
        this.updatePurchasePresentation();
      };
      this.onFormSubmit = (event) => this.addToCart(event);
      this.onBrowserOpen = () => this.openProductBrowser();
      this.onSuggestionsToggle = () => this.returnToSuggestions();
      this.onSearchInput = () => this.queueProductSearch();
      this.onSearchResultsClick = (event) => this.chooseSearchResult(event);
      this.onDialogClick = (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        const outside = event.clientX < bounds.left
          || event.clientX > bounds.right
          || event.clientY < bounds.top
          || event.clientY > bounds.bottom;
        if (outside) this.dialog.close();
      };
      this.onDialogClose = () => {
        this.cancelSearchWork();
        this.unlockPageScroll();
        this.productRequestId += 1;
        this.productController?.abort();
        this.productController = null;
        const opener = this.dialogOpener;
        this.dialogOpener = null;
        if (opener?.isConnected) window.setTimeout(() => opener.focus(), 0);
      };
      this.onDialogKeyDown = (event) => this.trapDialogFocus(event);
      this.onCartUpdate = (update = {}) => {
        this.applyCartUpdateSnapshot(update);
        this.reconcilingCart = true;
        this.rewardMessage.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        window.clearTimeout(this.cartUpdateTimer);
        this.cartUpdateTimer = window.setTimeout(() => this.refreshCartState(), 60);
      };
      this.onPageShow = (event) => {
        if (event.persisted) this.refreshCartState();
      };
      this.onVisibilityChange = () => {
        if (document.visibilityState === 'visible') this.refreshCartState();
      };
      this.onVariantChange = ({data} = {}) => {
        const variant = data?.variant;
        if (!variant?.id) return;
        this.mainVariantId = Number(variant.id);
      };

      this.choiceList.addEventListener('click', this.onChoiceClick);
      this.optionFields.addEventListener('click', this.onOptionChange);
      this.quantityInput.addEventListener('change', this.onQuantityChange);
      this.quantityInput.addEventListener('input', this.onQuantityChange);
      this.form.addEventListener('submit', this.onFormSubmit);
      this.browserButton.addEventListener('click', this.onBrowserOpen);
      this.suggestionsToggle.addEventListener('click', this.onSuggestionsToggle);
      this.searchInput.addEventListener('input', this.onSearchInput);
      this.searchResults.addEventListener('click', this.onSearchResultsClick);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.dialog.addEventListener('keydown', this.onDialogKeyDown);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      this.selectProduct(
        this.defaultProductHandle,
        undefined,
        {source: 'recommendation', reveal: false},
      );
      if (this.cartReady) {
        this.setCartReady(true);
        this.updateExperienceState();
        this.updateRewardState();
        this.updatePurchasePresentation();
      }
      window.requestAnimationFrame(() => {
        const renderedMainVariantId = this.readMainVariantId();
        if (renderedMainVariantId && renderedMainVariantId !== this.mainVariantId) {
          this.mainVariantId = renderedMainVariantId;
        }
      });
      this.refreshCartState();
      if (this.isAdding) this.setLoading(true);

      this.lifecycleObserver = new MutationObserver(() => {
        window.clearTimeout(this.reinitializeTimer);
        this.reinitializeTimer = window.setTimeout(() => {
          const markupChanged = this.form !== this.querySelector('[data-add-more-form]');
          const productChanged = this.currentProductHandle !== this.dataset.currentProductHandle;
          if (!markupChanged && !productChanged) return;
          this.disconnectedCallback();
          if (this.isConnected) this.connectedCallback();
        }, 0);
      });
      this.lifecycleObserver.observe(this, {
        childList: true,
        attributes: true,
        attributeFilter: ['data-current-product-handle', 'data-default-product-handle'],
      });
    }

    disconnectedCallback() {
      this.choiceList?.removeEventListener('click', this.onChoiceClick);
      this.optionFields?.removeEventListener('click', this.onOptionChange);
      this.quantityInput?.removeEventListener('change', this.onQuantityChange);
      this.quantityInput?.removeEventListener('input', this.onQuantityChange);
      this.form?.removeEventListener('submit', this.onFormSubmit);
      this.browserButton?.removeEventListener('click', this.onBrowserOpen);
      this.suggestionsToggle?.removeEventListener('click', this.onSuggestionsToggle);
      this.searchInput?.removeEventListener('input', this.onSearchInput);
      this.searchResults?.removeEventListener('click', this.onSearchResultsClick);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.dialog?.removeEventListener('keydown', this.onDialogKeyDown);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.unsubscribeCart?.();
      this.unsubscribeVariant?.();
      this.searchController?.abort();
      this.productController?.abort();
      this.cartRequestId += 1;
      this.cartController?.abort();
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.cartUpdateTimer);
      window.clearTimeout(this.reinitializeTimer);
      this.lifecycleObserver?.disconnect();
      this.unlockPageScroll();
      this.initialized = false;
    }

    normalizeProduct(product) {
      const optionNames = (product.option_names || product.options || []).map((option) =>
        typeof option === 'string' ? option : option.name
      );
      const variants = (product.variants || []).map((variant) => ({
        id: Number(variant.id),
        available: variant.available !== false,
        price: Number(variant.price || 0),
        compare_at_price: Number(variant.compare_at_price || 0),
        options: variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean),
      }));
      const image = typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.src || product.image || product.images?.[0] || '';
      return {
        id: Number(product.id),
        handle: product.handle,
        title: product.title,
        short_title: product.short_title || this.shortNames.get(product.handle) || '',
        url: product.url || `/products/${product.handle}`,
        image,
        option_names: optionNames,
        default_variant_id: Number(product.default_variant_id || variants.find((variant) => variant.available)?.id),
        variants,
      };
    }

    readMainVariantId() {
      const productInfo = this.closest('product-info') || document.querySelector('product-info[data-cca-master-template]');
      const variantInput = productInfo?.querySelector('product-form form [name="id"]');
      const variantId = Number(variantInput?.value);
      return Number.isFinite(variantId) && variantId > 0 ? variantId : 0;
    }

    get availableVariants() {
      return (this.selectedProduct?.variants || []).filter((variant) => variant.available);
    }

    get quantity() {
      if (!this.isSinglePieceVariant()) return 1;
      return Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1);
    }

    formatMoney(cents) {
      return this.moneyFormatter.format(Number(cents || 0) / 100);
    }

    packOptionIndex(product = this.selectedProduct) {
      return (product?.option_names || []).findIndex((name) => {
        const normalized = String(name).toLowerCase();
        return normalized.includes('pack') || normalized.includes('saver') || normalized.includes('quantity');
      });
    }

    optionRole(name = '') {
      const normalized = String(name).trim().toLowerCase();
      if (normalized.includes('pack') || normalized.includes('saver') || normalized.includes('quantity')) return 'saver-pack';
      if (normalized.includes('size')) return 'size';
      return '';
    }

    optionStepLabel(name, index) {
      const role = this.optionRole(name);
      if (role === 'saver-pack') return `${index + 2}. Choose Saver Pack:`;
      const usesMillimetreSizeLabel = role === 'size' && (
        String(name).trim().toLowerCase() === 'size'
        || this.selectedProduct?.handle === 'rectangular-silicone-felt-chair-glides'
      );
      if (usesMillimetreSizeLabel) return `${index + 2}. Choose Size (mm):`;
      return `${index + 2}. Choose ${String(name).trim()}:`;
    }

    optionDisplayValue(value, role) {
      const text = String(value).trim();
      if (role === 'size') return text.replace(/mm/gi, '').trim().replace(/-/g, '\u2013');
      if (role === 'saver-pack') return `${text.split(/x/i)[0].trim()}x`;
      return text;
    }

    isSinglePieceVariant(product = this.selectedProduct, variant = this.variant) {
      const packIndex = this.packOptionIndex(product);
      if (packIndex < 0 || !variant) return false;
      const value = String(variant.options[packIndex] || '').trim().toLowerCase();
      return /^1\s*x?\s*(piece|pc|unit)\b/.test(value);
    }

    splitProductTitle(title = '') {
      const separators = [' | ', ' - ']
        .map((separator) => ({separator, index: String(title).indexOf(separator)}))
        .filter(({index}) => index >= 0)
        .sort((left, right) => left.index - right.index);
      if (!separators.length) return {primary: String(title).trim(), support: ''};
      const {separator, index} = separators[0];
      return {
        primary: String(title).slice(0, index).trim(),
        support: String(title).slice(index + separator.length).trim(),
      };
    }

    productDisplayName(product = this.selectedProduct) {
      return product?.short_title
        || this.shortNames.get(product?.handle)
        || this.splitProductTitle(product?.title || '').primary;
    }

    preferredVariant(product, preferredVariantId) {
      const available = product.variants.filter((variant) => variant.available);
      const explicit = available.find((variant) => variant.id === Number(preferredVariantId));
      if (explicit) return explicit;
      const packIndex = this.packOptionIndex(product);
      const twelvePack = packIndex >= 0
        ? available.find((variant) => /^12\s*x\b/i.test(String(variant.options[packIndex])))
        : null;
      return twelvePack
        || available.find((variant) => variant.id === product.default_variant_id)
        || available[0]
        || product.variants[0];
    }

    selectProduct(handle, preferredVariantId, {source = 'recommendation', reveal = true} = {}) {
      const product = this.products.get(handle);
      if (!product) return;
      this.showError('');
      this.selectedProduct = product;
      this.variant = this.preferredVariant(
        product,
        preferredVariantId,
      );
      this.quantityInput.value = '1';
      this.selectedTitle.textContent = this.productDisplayName(product);
      this.selectedImage.src = product.image || '';
      this.selectedImage.hidden = !product.image;
      this.querySelectorAll('[data-product-choice]').forEach((choice) => {
        const selected = reveal && choice.dataset.productChoice === handle;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', String(selected));
      });
      this.isAlternateSelection = source === 'browser' && !this.recommendationHandles.has(handle);
      this.hasProductSelection = reveal;
      this.updateSelectionHierarchy();
      this.buildOptionFields();
      this.updatePurchasePresentation();
    }

    updateSelectionHierarchy() {
      this.classList.toggle('has-alternate-selection', this.isAlternateSelection);
      this.choiceList.hidden = Boolean(this.isAlternateSelection);
      this.selectedSummary.hidden = !this.isAlternateSelection;
      this.suggestionsToggle.hidden = !this.isAlternateSelection;
      this.browserLabel.textContent = 'Choose Another Product';
      this.browserButton.setAttribute(
        'aria-label',
        this.isAlternateSelection
          ? `Choose Another Product. Selected: ${this.productDisplayName()}`
          : 'Choose Another Product',
      );
    }

    returnToSuggestions() {
      if (!this.isAlternateSelection) return;
      this.selectProduct(
        this.defaultProductHandle,
        undefined,
        {source: 'recommendation', reveal: false},
      );
      this.choiceList.querySelector('[data-product-choice]')?.focus();
    }

    buildOptionFields() {
      this.optionFields.replaceChildren();
      const product = this.selectedProduct;
      if (!product || !this.variant) return;
      const hideDefaultTitle = product.option_names.length === 1
        && String(product.option_names[0]).toLowerCase() === 'title'
        && String(this.variant.options[0]).toLowerCase() === 'default title';
      if (hideDefaultTitle) {
        this.optionFields.hidden = true;
        return;
      }
      this.optionFields.hidden = false;
      product.option_names.forEach((name, index) => {
        const role = this.optionRole(name);
        const field = document.createElement('fieldset');
        field.className = `cca-add-more__option${role ? ` cca-add-more__option--${role}` : ''}`;
        const label = document.createElement('legend');
        label.textContent = this.optionStepLabel(name, index);
        const valuesWrap = document.createElement('div');
        valuesWrap.className = 'cca-add-more__option-values';
        const values = [...new Set(product.variants.map((variant) => variant.options[index]).filter(Boolean))];
        values.forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cca-add-more__option-pill';
          button.dataset.optionIndex = String(index);
          button.dataset.optionValue = value;
          button.textContent = this.optionDisplayValue(value, role);
          button.setAttribute('aria-label', value);
          const selected = this.variant.options[index] === value;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
          valuesWrap.append(button);
        });
        field.append(label, valuesWrap);
        this.optionFields.append(field);
      });
      this.updateOptionAvailability();
    }

    resolveVariant(changedIndex, changedValue) {
      const values = [...this.variant.options];
      values[changedIndex] = changedValue;
      let variant = this.availableVariants.find((candidate) =>
        candidate.options.every((option, index) => option === values[index])
      );
      if (!variant) {
        variant = this.availableVariants.find((candidate) =>
          candidate.options[changedIndex] === values[changedIndex]
          && candidate.options.slice(0, changedIndex).every((option, index) => option === values[index])
        ) || this.availableVariants.find((candidate) => candidate.options[changedIndex] === values[changedIndex]);
      }
      if (!variant) return;
      this.variant = variant;
      this.optionFields.querySelectorAll('button[data-option-index][data-option-value]').forEach((button) => {
        const selected = variant.options[Number(button.dataset.optionIndex)] === button.dataset.optionValue;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      this.updateOptionAvailability();
      this.updatePurchasePresentation();
    }

    updateOptionAvailability() {
      this.optionFields.querySelectorAll('button[data-option-index][data-option-value]').forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.optionValue;
        const available = this.availableVariants.some((candidate) => {
          if (candidate.options[index] !== value) return false;
          return candidate.options.every((option, optionIndex) =>
            optionIndex === index || optionIndex > index || option === this.variant.options[optionIndex]
          );
        });
        const globallyAvailable = this.availableVariants.some((candidate) => candidate.options[index] === value);
        button.disabled = !available;
        button.classList.toggle('is-sold-out', !globallyAvailable);
      });
    }

    taggedItems(cart) {
      return (cart?.items || []).filter((item) =>
        item.properties?._cca_add_more_offer === OFFER_TAG
        && item.properties?._cca_add_more_context === OFFER_CONTEXT
      );
    }

    isTaggedItem(item) {
      return item.properties?._cca_add_more_offer === OFFER_TAG
        && item.properties?._cca_add_more_context === OFFER_CONTEXT;
    }

    hasBasePurchase(cart) {
      return this.qualifyingBaseItems(cart).length > 0;
    }

    packSizeFromVariantTitle(title = '') {
      const match = String(title).match(/(?:^|\s\/\s)(\d+)\s*[x×]\s*(?:pack|pieces?)(?=\s*\/\s|$)/i);
      return match ? Number(match[1]) : 0;
    }

    qualifyingBaseItems(cart) {
      return (cart?.items || []).filter((item) =>
        item.properties?._cca_add_more_offer !== OFFER_TAG
        && this.packSizeFromVariantTitle(item.variant_title) >= MINIMUM_QUALIFYING_PACK
      );
    }

    lineSubtotal(item) {
      return Number(item.original_price || item.price || 0) * Number(item.quantity || 0);
    }

    qualifyingBaseSubtotal(cart) {
      return this.qualifyingBaseItems(cart).reduce(
        (total, item) => total + this.lineSubtotal(item),
        0,
      );
    }

    taggedSubtotal(cart) {
      return this.taggedItems(cart).reduce(
        (total, item) => total + this.lineSubtotal(item),
        0,
      );
    }

    updateExperienceState() {
      const unlocked = Boolean(
        this.cartReady
        && this.offerActive
        && this.hasBasePurchase(this.cart),
      );
      const state = !this.cartReady
        ? 'loading'
        : !unlocked
          ? 'locked'
          : this.hasProductSelection
            ? 'configured'
            : 'choices';
      this.dataset.experienceState = state;
      const showChoices = unlocked;
      const showForm = unlocked && this.hasProductSelection;
      const activeElement = document.activeElement;
      const focusWillBeHidden = (!showChoices && this.choiceSystem.contains(activeElement))
        || (!showForm && this.form.contains(activeElement));
      this.setRevealState(this.choiceReveal, this.choiceSystem, showChoices);
      this.setRevealState(this.formReveal, this.form, showForm);
      if (focusWillBeHidden) this.heading?.focus({preventScroll: true});
    }

    setRevealState(wrapper, content, expanded) {
      if (!wrapper || !content) return;
      wrapper.classList.toggle('is-collapsed', !expanded);
      wrapper.setAttribute('aria-hidden', String(!expanded));
      content.inert = !expanded;
    }

    async fetchCart(signal) {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: {Accept: 'application/json'},
        signal,
      });
      if (!response.ok) throw new Error('Your cart could not be checked. Refresh the page and try again.');
      return response.json();
    }

    applyCartUpdateSnapshot(update = {}) {
      if (update.source === 'cca-add-more-save') return;
      const cartData = update.cartData;
      if (!cartData) return;

      if (Array.isArray(cartData.items) && 'item_count' in cartData && 'total_price' in cartData) {
        this.cart = cartData;
      } else if (this.cartReady && this.cart && Number(cartData.variant_id || cartData.id)) {
        const items = [...(this.cart.items || [])];
        const lineIndex = cartData.key
          ? items.findIndex((item) => item.key === cartData.key)
          : -1;
        if (lineIndex >= 0) {
          const existing = items[lineIndex];
          items[lineIndex] = {
            ...existing,
            ...cartData,
            quantity: Math.max(Number(existing.quantity || 0), Number(cartData.quantity || 0)),
          };
        } else {
          items.push(cartData);
        }
        this.cart = {...this.cart, items};
      } else {
        return;
      }

      this.setCartReady(true);
      this.updateExperienceState();
      this.updateRewardState();
      this.updatePurchasePresentation();
    }

    async fetchCartSections(sectionIds) {
      if (!sectionIds.length) return null;
      for (const delay of [0, 120, 280]) {
        if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
        try {
          const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
            method: 'POST',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({
              updates: {},
              sections: sectionIds,
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) continue;
          const cart = await response.json();
          if (cart.sections) return cart.sections;
        } catch (_error) {
          // A verified add is rolled back if every authoritative refresh fails.
        }
      }
      return null;
    }

    async refreshCartState() {
      const requestId = ++this.cartRequestId;
      const hasAuthoritativeCart = Boolean(this.cartReady && this.cart);
      this.cartController?.abort();
      this.cartController = new AbortController();
      this.reconcilingCart = true;
      if (hasAuthoritativeCart) {
        this.rewardMessage.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
      }
      else this.setCartReady(false);
      try {
        const cart = await this.fetchCart(this.cartController.signal);
        if (requestId !== this.cartRequestId) return;
        this.cart = cart;
        this.reconcilingCart = false;
        this.setCartReady(true);
        this.showError('');
        this.updateExperienceState();
        this.updateRewardState();
        this.updatePurchasePresentation();
      } catch (error) {
        if (requestId !== this.cartRequestId) return;
        if (error.name === 'AbortError') return;
        this.reconcilingCart = false;
        this.setCartReady(false);
        this.updateExperienceState();
        this.updatePurchasePresentation();
        this.showError(error.message || 'Your cart could not be checked. Refresh and try again.');
      } finally {
        if (requestId === this.cartRequestId) this.cartController = null;
      }
    }

    setCartReady(ready) {
      this.cartReady = Boolean(ready);
      this.rewardMessage.dataset.cartStateReady = String(this.cartReady);
    }

    updateRewardState() {}

    updatePurchasePresentation() {
      if (!this.cartReady) {
        this.priceAnnouncement.textContent = '';
        this.priceWrap.hidden = true;
        this.quantityWrap.hidden = true;
        this.purchaseActions.hidden = true;
        this.purchaseActions.classList.remove('has-quantity');
        this.button.disabled = true;
        return;
      }
      this.updateExperienceState();
      if (!this.hasBasePurchase(this.cart)) {
        this.priceAnnouncement.textContent = '';
        this.priceWrap.hidden = true;
        this.quantityWrap.hidden = true;
        this.purchaseActions.hidden = true;
        this.purchaseActions.classList.remove('has-quantity');
        this.button.disabled = true;
        this.updateRewardState();
        return;
      }
      this.purchaseActions.hidden = false;
      if (!this.variant || this.variant.available === false) {
        this.button.disabled = true;
        this.buttonAction.textContent = this.variant ? 'Sold out' : 'Unavailable';
        this.basePrice.textContent = '';
        this.offerPrice.textContent = '';
        this.priceAnnouncement.textContent = '';
        this.priceWrap.hidden = true;
        this.quantityWrap.hidden = true;
        this.purchaseActions.classList.remove('has-quantity');
        return;
      }
      const cart = this.cart || {items: [], discount_codes: []};
      const quantity = this.quantity;
      const baseTotal = Number(this.variant.price) * quantity;
      const singlePiece = this.isSinglePieceVariant();
      const competingDiscount = this.hasCompetingNonOfferDiscount(cart);
      const existingSaving = this.expectedOfferSaving(cart);
      const prospectiveTaggedSubtotal = this.taggedSubtotal(cart) + baseTotal;
      const prospectiveSaving = Math.round(
        Math.min(this.qualifyingBaseSubtotal(cart), prospectiveTaggedSubtotal) * OFFER_PERCENT / 100,
      );
      const incrementalSaving = Math.max(0, prospectiveSaving - existingSaving);
      const discountedTotal = Math.max(0, baseTotal - incrementalSaving);
      const offerEligible = this.cartReady
        && this.offerActive
        && this.hasBasePurchase(cart)
        && !competingDiscount
        && incrementalSaving > 0;
      const canSubmit = this.cartReady
        && this.offerActive
        && this.hasBasePurchase(cart);

      this.priceWrap.hidden = false;
      if (!singlePiece) this.quantityInput.value = '1';
      this.quantityWrap.hidden = !singlePiece;
      this.purchaseActions.classList.toggle('has-quantity', singlePiece);

      this.priceWrap.classList.toggle('is-offer-ineligible', !offerEligible);
      this.basePrice.hidden = !offerEligible;
      this.basePrice.textContent = offerEligible ? this.formatMoney(baseTotal) : '';
      this.offerPrice.textContent = this.formatMoney(offerEligible ? discountedTotal : baseTotal);
      this.priceAnnouncement.textContent = offerEligible
        ? `Original price ${this.formatMoney(baseTotal)}. Estimated price after saving ${this.formatMoney(discountedTotal)}. You save ${this.formatMoney(incrementalSaving)}.`
        : `Selected product price ${this.formatMoney(baseTotal)} before any eligible cart discount.`;
      this.priceWrap.classList.remove('is-updating');
      window.requestAnimationFrame(() => {
        if (this.priceWrap?.isConnected) this.priceWrap.classList.add('is-updating');
      });
      this.updateRewardState();
      this.buttonAction.textContent = 'Add to cart';
      this.button.disabled = this.isAdding
        || this.reconcilingCart
        || !canSubmit;
    }

    lineDiscountTotal(item) {
      if (Number.isFinite(Number(item.line_level_total_discount))) {
        return Number(item.line_level_total_discount);
      }
      return (item.line_level_discount_allocations || []).reduce(
        (total, allocation) => total + Number(allocation.amount || 0),
        0,
      );
    }

    allocationTitles(item) {
      const allocationTitles = (item.line_level_discount_allocations || []).map(
        (allocation) => allocation.discount_application?.title,
      );
      const discountTitles = (item.discounts || []).map((discount) => discount.title);
      return [...allocationTitles, ...discountTitles].filter(Boolean).map((title) => title.toLowerCase());
    }

    isOfferTitle(title = '') {
      return title.includes(OFFER_TITLE_MATCH);
    }

    hasOfferApplied(cart) {
      return this.taggedItems(cart).some((item) =>
        this.allocationTitles(item).some((title) => this.isOfferTitle(title))
      );
    }

    hasCompetingNonOfferDiscount(cart) {
      const cartTitles = (cart?.cart_level_discount_applications || [])
        .map((application) => application.title || application.code)
        .filter(Boolean)
        .map((title) => String(title).toLowerCase());
      const lineTitles = (cart?.items || []).flatMap((item) => this.allocationTitles(item));
      return [...cartTitles, ...lineTitles].some((title) => !this.isOfferTitle(title));
    }

    expectedOfferSaving(cart) {
      const eligibleSubtotal = Math.min(
        this.qualifyingBaseSubtotal(cart),
        this.taggedSubtotal(cart),
      );
      return Math.round(eligibleSubtotal * OFFER_PERCENT / 100);
    }

    offerDiscountTotal(cart) {
      return this.taggedItems(cart).reduce((total, item) => {
        const allocations = item.line_level_discount_allocations || [];
        return total + allocations.reduce((lineTotal, allocation) => {
          const title = String(allocation.discount_application?.title || '').toLowerCase();
          return lineTotal + (this.isOfferTitle(title) ? Number(allocation.amount || 0) : 0);
        }, 0);
      }, 0);
    }

    verifyOffer(cart, submission, previousQuantity = 0) {
      const tagged = this.taggedItems(cart);
      const submittedLine = tagged.find((item) =>
        String(item.variant_id) === String(submission?.variantId)
        && Number(item.quantity || 0) >= Number(previousQuantity || 0) + Number(submission?.quantity || 0)
      );
      if (!submittedLine) return false;
      const expected = this.expectedOfferSaving(cart);
      const taggedCorrectly = tagged.length > 0
        && this.hasOfferApplied(cart)
        && Math.abs(this.offerDiscountTotal(cart) - expected) <= 1;
      const originalUntouched = (cart.items || []).filter((item) => !this.isTaggedItem(item)).every((item) =>
        this.allocationTitles(item).every((title) => !this.isOfferTitle(title))
      );
      const competingWinner = !taggedCorrectly
        && !this.hasOfferApplied(cart)
        && this.hasCompetingNonOfferDiscount(cart)
        && Number(cart?.total_discount || 0) >= this.expectedOfferSaving(cart);
      return originalUntouched && (taggedCorrectly || competingWinner);
    }

    async fetchVerifiedCart(submission, previousQuantity) {
      const delays = [0, 120, 280];
      let cart;
      for (const delay of delays) {
        if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
        cart = await this.fetchCart();
        if (this.verifyOffer(cart, submission, previousQuantity)) return cart;
      }
      return cart;
    }

    async rollbackAddition(resultKey, previousQuantity) {
      if (!resultKey) throw new Error('The added cart line could not be identified for rollback.');
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: resultKey, quantity: previousQuantity}),
      });
      if (!response.ok) throw new Error('The added cart line could not be rolled back.');
      const rolledBackCart = await response.json();
      const remainingLine = (rolledBackCart.items || []).find((item) => item.key === resultKey);
      if (Number(remainingLine?.quantity || 0) !== Number(previousQuantity || 0)) {
        throw new Error('The added cart line rollback could not be confirmed.');
      }
      return rolledBackCart;
    }

    async addToCart(event) {
      event.preventDefault();
      if (!this.variant || this.variant.available === false || this.isAdding) return;
      const addRequestId = ++this.addRequestId;
      const submission = {
        variantId: this.variant.id,
        quantity: this.quantity,
        productHandle: this.selectedProduct.handle,
      };
      this.isAdding = true;
      this.setLoading(true);
      this.showError('');
      let result;
      let priorCart;
      let priorTaggedLine;
      let sectionIds = [];
      let addedLineKey = '';
      let additionNeedsRollback = false;
      let additionRolledBack = false;

      try {
        priorCart = await this.fetchCart();
        if (!this.offerActive) throw new Error('This offer is unavailable right now.');
        if (!this.hasBasePurchase(priorCart)) throw new Error('Add a Saver Pack to your cart first.');
        priorTaggedLine = this.taggedItems(priorCart).find(
          (item) => String(item.variant_id) === String(submission.variantId),
        );

        const formData = new FormData();
        formData.append('id', String(submission.variantId));
        formData.append('quantity', String(submission.quantity));
        formData.append('properties[_cca_add_more_offer]', OFFER_TAG);
        formData.append('properties[_cca_add_more_context]', OFFER_CONTEXT);
        const cartComponent = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartComponent?.getSectionsToRender) {
          sectionIds = cartComponent.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
          cartComponent.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json'},
          body: formData,
        });
        result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'This product could not be added.');

        addedLineKey = result.key || result.items?.[0]?.key;
        additionNeedsRollback = true;

        const previousQuantity = Number(priorTaggedLine?.quantity || 0);
        const verifiedCart = await this.fetchVerifiedCart(submission, previousQuantity);
        if (!this.verifyOffer(verifiedCart, submission, previousQuantity)) {
          await this.rollbackAddition(addedLineKey, Number(priorTaggedLine?.quantity || 0));
          additionRolledBack = true;
          additionNeedsRollback = false;
          throw new Error('The saving could not be confirmed, so the product was not added. Please try again.');
        }

        const refreshedSections = await this.fetchCartSections(sectionIds);
        if (sectionIds.length && !refreshedSections) {
          throw new Error('The updated cart could not be displayed.');
        }
        additionNeedsRollback = false;
        if (!this.isConnected || addRequestId !== this.addRequestId) return;
        this.cart = verifiedCart;
        this.cartRequestId += 1;
        this.setCartReady(true);
        if (refreshedSections) result.sections = refreshedSections;
        if (cartComponent?.renderContents) cartComponent.renderContents(result, submission.quantity);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cca-add-more-save',
            productVariantId: String(submission.variantId),
            cartData: result,
          });
        }
        this.updateRewardState();
        this.updatePurchasePresentation();
      } catch (error) {
        if (addRequestId !== this.addRequestId) return;
        let message = error.message || 'Something went wrong. Please try again.';
        if (additionNeedsRollback && !additionRolledBack) {
          try {
            await this.rollbackAddition(addedLineKey, Number(priorTaggedLine?.quantity || 0));
            additionRolledBack = true;
            message = 'The saving could not be confirmed, so the product was not added. Please try again.';
          } catch (rollbackError) {
            console.error('The product could not be rolled back after the saving failed.', rollbackError);
            message = 'The saving could not be confirmed. Please check your cart before trying again.';
          }
        }
        if (!this.isConnected || addRequestId !== this.addRequestId) return;
        await this.refreshCartState();
        this.showError(message);
      } finally {
        if (addRequestId === this.addRequestId) {
          this.isAdding = false;
          this.setLoading(false);
        }
      }
    }

    setLoading(loading) {
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', String(loading));
      const controls = this.querySelectorAll(
        '[data-product-choice], [data-product-browser-open], [data-suggestions-toggle], '
        + 'button[data-option-index], [data-quantity-wrap] button, [data-quantity-input]',
      );
      controls.forEach((control) => {
        if (loading) {
          control.dataset.ccaAddMoreWasDisabled = String(control.disabled);
          control.disabled = true;
        } else if ('ccaAddMoreWasDisabled' in control.dataset) {
          control.disabled = control.dataset.ccaAddMoreWasDisabled === 'true';
          delete control.dataset.ccaAddMoreWasDisabled;
        }
      });
      if (!loading) this.updateOptionAvailability();
      this.updatePurchasePresentation();
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    openProductBrowser() {
      if (this.dialog.open) return;
      this.dialogOpener = document.activeElement;
      this.cancelSearchWork();
      this.productRequestId += 1;
      this.productController?.abort();
      this.productController = null;
      this.searchInput.value = '';
      this.searchResults.replaceChildren();
      this.searchStatus.textContent = 'Loading products...';
      this.lockPageScroll();
      this.dialog.showModal();
      window.setTimeout(() => this.searchInput.focus(), 0);
      this.loadSuggestedProducts();
    }

    lockPageScroll() {
      if (this.pageScrollLock) return;
      const body = document.body;
      this.pageScrollLock = {
        scrollY: window.scrollY,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        paddingRight: body.style.paddingRight,
      };
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const currentPaddingRight = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.position = 'fixed';
      body.style.top = `-${this.pageScrollLock.scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      if (scrollbarWidth) body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    unlockPageScroll() {
      if (!this.pageScrollLock) return;
      const body = document.body;
      const lock = this.pageScrollLock;
      body.style.position = lock.position;
      body.style.top = lock.top;
      body.style.left = lock.left;
      body.style.right = lock.right;
      body.style.width = lock.width;
      body.style.paddingRight = lock.paddingRight;
      this.pageScrollLock = null;
      window.scrollTo(0, lock.scrollY);
    }

    trapDialogFocus(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dialog.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )].filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    queueProductSearch() {
      window.clearTimeout(this.searchTimer);
      this.searchController?.abort();
      const requestId = ++this.searchRequestId;
      const query = this.searchInput.value.trim();
      this.searchTimer = window.setTimeout(() => {
        if (requestId !== this.searchRequestId) return;
        if (query.length < 2) this.loadSuggestedProducts(requestId);
        else this.searchProducts(query, requestId);
      }, 220);
    }

    cancelSearchWork() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
      this.searchRequestId += 1;
      this.searchController?.abort();
      this.searchController = null;
    }

    async loadSuggestedProducts(requestId = ++this.searchRequestId) {
      this.searchController?.abort();
      this.searchController = new AbortController();
      this.searchStatus.textContent = 'Loading products...';
      try {
        const response = await fetch(
          `${window.routes?.product_recommendations_url || '/recommendations/products'}?product_id=${this.dataset.currentProductId}&limit=8&intent=related`,
          {headers: {Accept: 'application/json'}, signal: this.searchController.signal},
        );
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (requestId !== this.searchRequestId) return;
        this.renderSearchResults(data.products || []);
      } catch (error) {
        if (error.name !== 'AbortError' && requestId === this.searchRequestId) {
          this.searchStatus.textContent = 'Search for a product by name.';
          this.searchResults.replaceChildren();
        }
      }
    }

    async searchProducts(query, requestId = ++this.searchRequestId) {
      this.searchController?.abort();
      this.searchController = new AbortController();
      this.searchStatus.textContent = 'Searching...';
      try {
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'hide',
        });
        const response = await fetch(`/search/suggest.json?${params}`, {
          headers: {Accept: 'application/json'},
          signal: this.searchController.signal,
        });
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (requestId !== this.searchRequestId) return;
        this.renderSearchResults(data.resources?.results?.products || []);
      } catch (error) {
        if (error.name !== 'AbortError' && requestId === this.searchRequestId) {
          this.searchStatus.textContent = 'Products could not be loaded. Try another search.';
          this.searchResults.replaceChildren();
        }
      }
    }

    renderSearchResults(products) {
      const eligible = products.filter((product) =>
        product.handle
        && !this.isExcludedProductHandle(product.handle)
        && product.available !== false
      );
      this.searchResults.replaceChildren();
      if (!eligible.length) {
        this.searchStatus.textContent = 'No matching products found.';
        return;
      }
      this.searchStatus.textContent = `${eligible.length} ${eligible.length === 1 ? 'product' : 'products'}`;
      eligible.forEach((product) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cca-add-more__search-result';
        button.dataset.searchProductHandle = product.handle;
        const selected = product.handle === this.selectedProduct?.handle;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        const image = document.createElement('img');
        const imageSource = typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image?.url || product.featured_image?.src || product.image || '';
        image.src = imageSource;
        image.alt = '';
        image.width = 72;
        image.height = 72;
        image.hidden = !imageSource;
        const copy = document.createElement('span');
        copy.className = 'cca-add-more__search-result-copy';
        const splitTitle = this.splitProductTitle(product.title);
        const title = document.createElement('strong');
        title.textContent = splitTitle.primary;
        copy.append(title);
        if (splitTitle.support) {
          const support = document.createElement('span');
          support.className = 'cca-add-more__search-result-support';
          support.textContent = splitTitle.support;
          copy.append(support);
        }
        const action = document.createElement('span');
        action.className = 'cca-add-more__search-result-action';
        action.textContent = selected ? 'Selected' : 'Choose';
        button.append(image, copy, action);
        this.searchResults.append(button);
      });
    }

    async chooseSearchResult(event) {
      const button = event.target.closest('[data-search-product-handle]');
      if (!button) return;
      const handle = button.dataset.searchProductHandle;
      const requestId = ++this.productRequestId;
      this.productController?.abort();
      this.productController = new AbortController();
      button.disabled = true;
      this.searchStatus.textContent = 'Loading options...';
      try {
        let product = this.products.get(handle);
        if (!product) {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            headers: {Accept: 'application/json'},
            signal: this.productController.signal,
          });
          if (!response.ok) throw new Error();
          product = this.normalizeProduct(await response.json());
          if (this.isExcludedProductHandle(product.handle)) throw new Error();
          this.products.set(product.handle, product);
        }
        if (requestId !== this.productRequestId) return;
        this.selectProduct(product.handle, undefined, {source: 'browser'});
        this.dialog.close();
        this.browserButton.focus();
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.productRequestId) return;
        button.disabled = false;
        this.searchStatus.textContent = 'That product could not be loaded. Choose another product.';
      }
    }

    isExcludedProductHandle(handle = '') {
      const normalized = String(handle).trim().toLowerCase();
      return normalized === 'sample-pack-new' || normalized.includes('sample-pack');
    }
  }

  customElements.define('cca-add-more-save', CcaAddMoreSave);
})();
